import { useState, useEffect } from 'react';
import axios from 'axios';
import { FiPackage, FiClock, FiTruck, FiRefreshCw } from 'react-icons/fi';
import BatchDeliveryModal from '@/components/admin/BatchDeliveryModal';
import DeliveryRouteModal from '@/components/admin/DeliveryRouteModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmtChild = (r) => (r.is_child_hold ? ` (${r.child_name || 'Child'} — Children membership)` : '');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '');

function Column({ icon, title, accent, rows, emptyText, metaFn, testid, action, busyId, onAction }) {
    return (
        <div className={`bg-white rounded-lg border-2 ${accent} p-4 flex flex-col`} data-testid={testid}>
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 font-semibold text-gray-800">{icon}<span>{title}</span></div>
                <span className="text-sm font-bold bg-gray-100 text-gray-700 rounded-full px-2.5 py-0.5">{rows.length}</span>
            </div>
            {rows.length === 0 ? (
                <p className="text-sm text-gray-400 italic">{emptyText}</p>
            ) : (
                <ul className="space-y-2 max-h-56 overflow-y-auto pr-1">
                    {rows.map((r) => { const rowId = r.hold_id || r.pickup_id; return (
                        <li key={rowId} className="text-sm border-b border-gray-100 pb-1.5 flex items-start justify-between gap-2" data-testid={`qa-row-${rowId}`}>
                            <div className="min-w-0">
                                <span className="font-medium text-gray-800">{r.member_name}{fmtChild(r)}</span>
                                <span className="text-gray-500"> — {r.book_title}</span>
                                <span className="block text-xs text-gray-400">{metaFn(r)}</span>
                            </div>
                            {action && (() => { const a = typeof action === 'function' ? action(r) : action; if (!a) return null; return (
                                <button
                                    onClick={() => onAction(r)}
                                    disabled={busyId === rowId}
                                    className={`shrink-0 text-xs font-semibold text-white rounded px-2.5 py-1.5 ${a.className} disabled:opacity-50`}
                                    data-testid={`qa-action-${rowId}`}
                                >
                                    {busyId === rowId ? '…' : a.label}
                                </button>
                            ); })()}
                        </li>
                    ); })}
                </ul>
            )}
        </div>
    );
}

export default function AdminQuickActions() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [feedback, setFeedback] = useState(null);
    const [deliveryModalHold, setDeliveryModalHold] = useState(null);
    const [showRoute, setShowRoute] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/admin/quick-actions`, { headers: getAuthHeader() });
            setData(res.data);
        } catch { /* silent — panel is a convenience */ }
        setLoading(false);
    };

    useEffect(() => { fetchData(); }, []);

    const runAction = async (row, path, okText) => {
        setBusyId(row.hold_id);
        setFeedback(null);
        try {
            await axios.put(`${API}${path}`, {}, { headers: getAuthHeader() });
            setFeedback({ ok: true, text: `${okText}: ${row.member_name} — ${row.book_title}` });
            await fetchData();
            window.dispatchEvent(new CustomEvent('quick-action-done'));
        } catch (e) {
            setFeedback({ ok: false, text: e.response?.data?.detail || 'Action failed — try from Manage Holds.' });
        }
        setBusyId(null);
    };

    const completePickupReq = async (row) => {
        const input = window.prompt('Payment amount collected for this pickup (₹). Enter 0 if none:', '0');
        if (input === null) return;
        const charge = parseFloat(input);
        if (isNaN(charge) || charge < 0) { setFeedback({ ok: false, text: 'Please enter a valid amount (0 or more)' }); return; }
        setBusyId(row.pickup_id);
        setFeedback(null);
        try {
            await axios.put(`${API}/admin/pickups/${row.pickup_id}/complete?charge_amount=${charge}`, {}, { headers: getAuthHeader() });
            setFeedback({ ok: true, text: `Pickup completed: ${row.member_name}${charge > 0 ? ` — ₹${charge} recorded` : ''}` });
            await fetchData();
            window.dispatchEvent(new CustomEvent('quick-action-done'));
        } catch (e) {
            setFeedback({ ok: false, text: e.response?.data?.detail || 'Action failed — try from Manage Holds.' });
        }
        setBusyId(null);
    };

    if (loading && !data) {
        return <div className="mb-8 text-sm text-gray-400" data-testid="quick-actions-loading">Loading today&apos;s snapshot…</div>;
    }
    if (!data) return null;
    const routeDeliveries = (data.deliveries_today || []).filter((r) => r.type !== 'pickup_request');

    return (
        <div className="mb-8" data-testid="admin-quick-actions">
            <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-bold text-gray-800">Today at the library <span className="text-sm font-normal text-gray-400">— {data.date}</span></h2>
                <div className="flex items-center gap-4">
                    {routeDeliveries.length > 0 && (
                        <button onClick={() => setShowRoute(true)} className="text-sm text-purple-700 hover:text-purple-900 font-semibold" data-testid="delivery-route-btn">
                            🛵 Route view
                        </button>
                    )}
                    <button onClick={fetchData} className="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1" data-testid="quick-actions-refresh">
                        <FiRefreshCw className={loading ? 'animate-spin' : ''} /> Refresh
                    </button>
                </div>
            </div>
            {feedback && (
                <div className={`mb-3 text-sm rounded px-3 py-2 ${feedback.ok ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'}`} data-testid="quick-actions-feedback">
                    {feedback.text}
                </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Column
                    icon={<FiPackage className="text-emerald-600" />}
                    title="Pickups today"
                    accent="border-emerald-200"
                    rows={data.pickups_today}
                    emptyText="No pickups scheduled for today."
                    metaFn={(r) => (r.pickup_request ? `Pickup request${r.status === 'pending' ? ' — prepare book' : ''}` : (r.delivery_option === 'paid_delivery' ? 'Delivery order' : 'Pickup box'))}
                    testid="qa-pickups"
                    action={(r) => (r.status === 'reserved' ? { label: '✓ Picked up', className: 'bg-emerald-600 hover:bg-emerald-700' } : null)}
                    busyId={busyId}
                    onAction={(r) => runAction(r, `/holds/${r.hold_id}/borrow`, 'Marked picked up')}
                />
                <Column
                    icon={<FiClock className="text-amber-600" />}
                    title="Returns due today"
                    accent="border-amber-200"
                    rows={data.returns_due_today}
                    emptyText="Nothing due back today."
                    metaFn={(r) => `Due ${fmtDate(r.due_date)}`}
                    testid="qa-returns"
                    action={{ label: '✓ Returned', className: 'bg-amber-600 hover:bg-amber-700' }}
                    busyId={busyId}
                    onAction={(r) => runAction(r, `/holds/${r.hold_id}/return`, 'Return recorded')}
                />
                <Column
                    icon={<FiTruck className="text-purple-600" />}
                    title="Deliveries today"
                    accent="border-purple-200"
                    rows={data.deliveries_today || []}
                    emptyText="No deliveries going out today."
                    metaFn={(r) => (r.type === 'pickup_request' ? `Pickup request${r.delivery_address ? ` — ${r.delivery_address}` : ''}` : (r.delivery_address || 'Delivery order'))}
                    testid="qa-deliveries"
                    action={(r) => (r.type === 'pickup_request' ? { label: '✓ Picked up', className: 'bg-teal-600 hover:bg-teal-700' } : { label: '✓ Delivered', className: 'bg-purple-600 hover:bg-purple-700' })}
                    busyId={busyId}
                    onAction={(r) => (r.type === 'pickup_request' ? completePickupReq(r) : setDeliveryModalHold({ id: r.hold_id, user_id: r.user_id }))}
                />
            </div>
            {showRoute && <DeliveryRouteModal deliveries={routeDeliveries} onClose={() => setShowRoute(false)} />}
            {deliveryModalHold && (
                <BatchDeliveryModal
                    hold={deliveryModalHold}
                    getAuthHeader={getAuthHeader}
                    onClose={() => setDeliveryModalHold(null)}
                    onSuccess={async (res) => {
                        const bal = typeof res?.new_balance === 'number' ? ` New balance: ₹${res.new_balance.toFixed(2)}.` : '';
                        setFeedback({ ok: true, text: `${res?.message || 'Holds delivered'}.${bal}` });
                        setDeliveryModalHold(null);
                        await fetchData();
                        window.dispatchEvent(new CustomEvent('quick-action-done'));
                    }}
                />
            )}
        </div>
    );
}
